import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@abodeminehq/parcel-ui/icons/fa/pro-solid";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  HStack,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Separator,
  Spinner,
} from "@abodeminehq/parcel-ui";
import { createFileRoute } from "@tanstack/react-router";
import {
  SummeryContentLayout,
  SummeryContentLayoutContent,
  SummeryContentLayoutHeader,
} from "~/components/layouts/SummeryContentLayout";
import { ApiDocumentationViewer } from "~/components/app/documentation/api-documentation-viewer";
import { useEffect, useState } from "react";
import { useShortLivedToken } from "~/lib/queries/tokens";
import { useOrg } from "~/contexts/org-context";
import { useAuthScopes } from "~/hooks/use-auth-scopes";
import { useAuthContext } from "~/contexts/auth-context";
import { LogtoScopes } from "~/lib/logto/logto";
import { ScopeRule } from "~/lib/scopes";

export const Route = createFileRoute("/org/$org/docs")({
  component: OrgDocsPage,
});

const ORG_SHORT_LIVED_TOKEN_SCOPE = ScopeRule.ORG(
  LogtoScopes.OrgShortLivedTokenCreate,
);

function OrgDocsPage() {
  const { activeOrg, activeOrgId, isAdminMode } = useOrg();
  const { activeOrg: authOrgId, isLoading: isLoadingAuth } = useAuthContext();
  const pageTitle = "Documentation";
  const orgName = activeOrg?.name;

  const orgIdForToken = activeOrgId ?? authOrgId;

  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const handleDisclaimerChange = (open: boolean) => {
    setShowDisclaimer(open);
    if (!open) {
      localStorage.setItem(
        "tutorial_shown_playground_token_disclaimer",
        "true",
      );
    }
  };

  const hasShortLivedTokenAccess = useAuthScopes(ORG_SHORT_LIVED_TOKEN_SCOPE);
  const hasAdminAccess = useAuthScopes(LogtoScopes.AdminAccess);
  const canGetToken = hasShortLivedTokenAccess || (isAdminMode && hasAdminAccess);

  const {
    data: shortLivedToken,
    isLoading: isLoadingToken,
    isError,
  } = useShortLivedToken(orgIdForToken, {
    enabled: canGetToken && !isLoadingAuth && !!orgIdForToken,
  });

  useEffect(() => {
    if (
      !localStorage.getItem("tutorial_shown_playground_token_disclaimer") &&
      orgIdForToken &&
      canGetToken &&
      shortLivedToken
    ) {
      const timer = setTimeout(() => setShowDisclaimer(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [orgIdForToken, canGetToken, shortLivedToken]);

  return (
    <>
      <SummeryContentLayout className="h-screen w-full">
        <SummeryContentLayoutHeader
          title={isAdminMode ? (orgName ?? pageTitle) : pageTitle}
          subtitle={isAdminMode ? pageTitle : orgName}
        >
          <HStack>
            {isLoadingToken && (
              <Spinner size={20} className="text-muted-foreground" />
            )}

            {isError && (
              <HoverCard>
                <HoverCardTrigger>
                  <FontAwesomeIcon icon={faTriangleExclamation} className="h-5 w-5 text-destructive" />
                </HoverCardTrigger>
                <HoverCardContent>
                  <HStack alignment="top">
                    <FontAwesomeIcon icon={faTriangleExclamation}
                      className="h-32 w-32 text-destructive"
                    />
                    <p>
                      There was an issue generating a playground token for this
                      organization.
                      <Separator />
                      Contact your organization owner to request access.
                    </p>
                  </HStack>
                </HoverCardContent>
              </HoverCard>
            )}

            {!canGetToken && orgIdForToken && (
              <HoverCard>
                <HoverCardTrigger>
                  <FontAwesomeIcon icon={faTriangleExclamation} className="h-5 w-5 text-destructive" />
                </HoverCardTrigger>
                <HoverCardContent>
                  <HStack alignment="top">
                    <FontAwesomeIcon icon={faTriangleExclamation}
                      className="h-32 w-32 text-destructive"
                    />
                    <p>
                      You do not have permission to make calls to the API from
                      this organization.
                      <Separator />
                      Contact your organization owner to request access.
                    </p>
                  </HStack>
                </HoverCardContent>
              </HoverCard>
            )}
          </HStack>
        </SummeryContentLayoutHeader>
        <ApiDocumentationViewer
          className="h-full w-full"
          token={shortLivedToken?.token ?? null}
        />
      </SummeryContentLayout>

      <Dialog open={showDisclaimer} onOpenChange={handleDisclaimerChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>API Playground Disclaimer</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            <p>
              We have provided you with a temporary token to use the API
              Playground. This token is valid for one hour. It is not to be used
              in production.
            </p>
            <Separator />
            <b>
              This short lived token will still contribute to your
              organization's usage and ratelimits.
            </b>
            <p>You can disable this in the dropdown at the top of the page</p>
          </DialogDescription>
        </DialogContent>
      </Dialog>
    </>
  );
}
